import React, { useState, useEffect, useCallback } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import { useNavigate } from "react-router-dom";
import FileInput from "../../components/fileInput";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import Textarea from '../../components/commonTextArea';
import { Formik } from "formik";
import { useDispatch } from "react-redux";
import { Toast, Form, Col, Row, Card } from "react-bootstrap";
import { organizationAddSchema } from "../../schema/validationSchemas";
import {
  addOrganization,
  getBusinessTypes,
  getOrganizationTypes,
} from "../Organization/organizationSlice";
import Editor from "../../components/commonEditor";
import SimpleDropdown from "../../components/simpleDropdown";

export default function OrganizationAddNew() {
  const navigate = useNavigate();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [logoPreview, setLogoPreview] = useState(null);
  const [favIconPreview, setFavIconPreview] = useState(null);
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [organizationTypeOptions, setOrganizationTypeOptions] = useState([]);
  // const [tagsOptions, setTagsOptions] = useState([]);
  const dispatch = useDispatch();

  const planStatusOptions = [
    {value: 'active', label: 'Active'},
    {value: 'expired', label: 'Expired'},
  ];

  const initialEventState = {
    user_name: "",
    email: "",
    password: "",
    password_confirmation: "",
    business_type_id: "",
    organization_type_id: "",
    company_name: "",
    ceo_name: "",
    domain_name: "",
    data_key: "",
    image_folder_name: "",
    phone_number: "",
    address: "",
    logo: "",
    favicon_icon: "",
    description: "",
    plan_start_date: "",
    plan_end_date: "",
    plan_status: "",
  };

  // const fetchTags = useCallback(async () => {
  //   try {
  //     await dispatch(getOrganizations({})).then((res) => {
  //       if (res.payload) {
  //         if (res.payload.length > 0) {
  //           const formattedData = res.payload.map((legalFirm) => ({
  //             value: legalFirm.id,
  //             label: legalFirm.name,
  //           }));
  //           setTagsOptions(formattedData);
  //         }
  //       } else {
  //         setTagsOptions([]);
  //       }
  //     });
  //   } catch (error) {
  //     console.error("Error fetching categories:", error);
  //   }
  // }, [dispatch]);

  // useEffect(() => {
  //   fetchTags();
  // }, [fetchTags]);

  const fetchBusinessTypes = useCallback(async () => {
    try {
      await dispatch(getBusinessTypes({})).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((businessType) => ({
              value: businessType.id,
              label: businessType.name,
            }));
            setBusinessTypeOptions(formattedData);
          }
        } else {
          setBusinessTypeOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching business type:", error);
    }
  }, [dispatch]);

  const fetchOrganizationTypes = useCallback(async () => {
    try {
      await dispatch(getOrganizationTypes({})).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((organizationType) => ({
              value: organizationType.id,
              label: organizationType.name,
            }));
            setOrganizationTypeOptions(formattedData);
          }
        } else {
          setOrganizationTypeOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching organization type:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchBusinessTypes();
    fetchOrganizationTypes();
  }, [fetchBusinessTypes, fetchOrganizationTypes]);

  const handleModalClose = () => {
    navigate("/organizations");
  };

  const handleImageChange = (file, setFieldValue) => {
    if (file) {
      setLogoPreview(URL.createObjectURL(file)); // Generate preview URL
      setFieldValue("logo", file); // Set the file in Formik
    }
  };

  const handleFavIconChange = (file, setFieldValue) => {
    if (file) {
      setFavIconPreview(URL.createObjectURL(file)); // Generate preview URL
      setFieldValue("favicon_icon", file); // Set the file in Formik
    }
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    await dispatch(addOrganization(values))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem(
            "organization_notify_message",
            res.payload.message
          );
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          navigate("/organizations");
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(
            res.payload.message || "Failed to add organization!"
          );
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setSnackbarMessage("Failed to add organization!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>
      <Formik
        initialValues={initialEventState}
        validationSchema={organizationAddSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          handleChange,
          handleSubmit,
          values,
          setFieldValue,
        }) => (
          <Form
            className="bg-white p-4 rounded-2 shadow-sm "
            onSubmit={handleSubmit}
            encType="multipart/form-data"
          >
            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">Basic Details</h4>
              <Row>
                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="user_name" title="User Name" />
                  <Input
                    id="user_name"
                    name="user_name"
                    type="text"
                    placeholder="Enter User Name"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.user_name && touched.user_name
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.user_name && errors?.user_name && (
                    <ValidationFeedback title={errors?.user_name} />
                  )}
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="email" title="Email" />
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.email && touched.email ? "error" : "correct"
                    }`}
                  />
                  {touched.email && errors?.email && (
                    <ValidationFeedback title={errors?.email} />
                  )}
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel title="Password" />
                  <div className="position-relative">
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Enter your password"
                      onChange={handleChange}
                      className={`shadow-none ${
                        touched.password && errors.password
                          ? "error"
                          : "correct"
                      }`}
                    />
                  </div>
                  {touched.password && errors.password ? (
                    <ValidationFeedback title={errors.password} />
                  ) : null}
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel title="Confirm Password" />
                  <div className="position-relative">
                    <Input
                      id="password_confirmation"
                      name="password_confirmation"
                      type="password"
                      placeholder="Confirm your password"
                      onChange={handleChange}
                      className={`shadow-none ${
                        touched.password_confirmation &&
                        errors.password_confirmation
                          ? "error"
                          : "correct"
                      }`}
                    />
                  </div>
                  {touched.password_confirmation &&
                  errors.password_confirmation ? (
                    <ValidationFeedback title={errors.password_confirmation} />
                  ) : null}
                </Col>
              </Row>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">Company Details</h4>
              <Row>
                <Col md={6} className="mb-4">
                  <CommonFormLabel
                    htmlFor="company_name"
                    title="Company Name"
                  />
                  <Input
                    id="company_name"
                    name="company_name"
                    type="text"
                    placeholder="Enter Company Name"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.company_name && touched.company_name
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.company_name && errors?.company_name && (
                    <ValidationFeedback title={errors?.company_name} />
                  )}
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="ceo_name" title="CEO Name" />
                  <Input
                    id="ceo_name"
                    name="ceo_name"
                    type="text"
                    placeholder="Enter CEO Name"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.ceo_name && touched.ceo_name ? "error" : "correct"
                    }`}
                  />
                  {touched.ceo_name && errors?.ceo_name && (
                    <ValidationFeedback title={errors?.ceo_name} />
                  )}
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="phone_number" title="Phone Number" />
                  <Input
                    id="phone_number"
                    name="phone_number"
                    type="text"
                    placeholder="Enter Phone Number"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.phone_number && touched.phone_number ? "error" : "correct"
                    }`}
                  />
                  {touched.phone_number && errors?.phone_number && (
                    <ValidationFeedback title={errors?.phone_number} />
                  )}
                </Col>

                <Col md={6} className="mb-4">
                  <SimpleDropdown
                    label="Business Type"
                    name="business_type_id"
                    value={values.business_type_id}
                    onChange={(selectedOption) => {
                      setFieldValue("business_type_id", selectedOption.value);
                    }}
                    options={businessTypeOptions}
                    placeholder="Select Business Type"
                    error={errors?.business_type_id}
                    touched={touched.business_type_id}
                    // className={'mb-0'}
                  />
                  {/* {touched.business_type_id && errors?.business_type_id && (
                    <ValidationFeedback title={errors?.business_type_id} />
                  )} */}
                </Col>

                <Col md={6} className="mb-4">
                  <SimpleDropdown
                    label="Organization Type"
                    name="organization_type_id"
                    value={values.organization_type_id}
                    onChange={(selectedOption) => {
                      setFieldValue(
                        "organization_type_id",
                        selectedOption.value
                      );
                    }}
                    options={organizationTypeOptions}
                    placeholder="Select Organization Type"
                    error={errors?.organization_type_id}
                    touched={touched.organization_type_id}
                    // className={'mb-0'}
                  />
                  {/* {touched.organization_type_id && errors?.organization_type_id && (
                    <ValidationFeedback title={errors?.organization_type_id} />
                  )} */}
                </Col>

                <Col md={12} className="mb-4">
                  <CommonFormLabel htmlFor="address" title="Address" />
                  <Textarea
                    id="address"
                    name="address"
                    placeholder="Enter Address"
                    onChange={handleChange}
                    value={values.address}
                    className={`shadow-none ${
                      errors?.address && touched.address ? "error" : "correct"
                    }`}
                    rows={6}
                  />
                  {touched.address && errors?.address && (
                    <ValidationFeedback title={errors?.address} />
                  )}
                </Col>

                <Col md={12} className="mb-4">
                  <CommonFormLabel htmlFor="description" title="Description" />
                  <Editor
                    value={values.description}
                    onChange={(content) =>
                      setFieldValue("description", content)
                    }
                    height="250px"
                  />
                  {touched.description && errors?.description && (
                    <ValidationFeedback title={errors?.description} />
                  )}
                </Col>
              </Row>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">
                Website Configuration Data
              </h4>
              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel htmlFor="domain_name" title="Domain Name" />
                  <Input
                    id="domain_name"
                    name="domain_name"
                    type="text"
                    placeholder="Enter Domain Name"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.domain_name && touched.domain_name
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.domain_name && errors?.domain_name && (
                    <ValidationFeedback title={errors?.domain_name} />
                  )}
                </Col>

                <Col md={12} className="mb-4">
                  <CommonFormLabel
                    htmlFor="data_key"
                    title="Organization Key"
                  />
                  <CommonFormLabel
                    className="ms-2 text-primary"
                    htmlFor="data_key_note"
                    title="(Note:- This Organization Key used in website to get the related organization data)"
                  />
                  <Input
                    id="data_key"
                    name="data_key"
                    type="text"
                    placeholder="Enter Organization Key"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.data_key && touched.data_key ? "error" : "correct"
                    }`}
                  />
                  {touched.data_key && errors?.data_key && (
                    <ValidationFeedback title={errors?.data_key} />
                  )}
                </Col>

                <Col md={12} className="mb-4">
                  <CommonFormLabel
                    htmlFor="image_folder_name"
                    title="Image Folder Name"
                  />
                  <CommonFormLabel
                    className="ms-2 text-primary"
                    htmlFor="image_folder_name_note"
                    title="(Note:- This image folder name used to store images in specific folder of the digital ocean space)"
                  />
                  <Input
                    id="image_folder_name"
                    name="image_folder_name"
                    type="text"
                    placeholder="Enter Image Folder Name"
                    onChange={handleChange}
                    className={`shadow-none ${
                      errors?.image_folder_name && touched.image_folder_name
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.image_folder_name && errors?.image_folder_name && (
                    <ValidationFeedback title={errors?.image_folder_name} />
                  )}
                </Col>
              </Row>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">Plan Details</h4>
              <Row>
                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="plan_start_date" title="Plan Start Date" />
                  <Input
                    id="plan_start_date"
                    name="plan_start_date"
                    type="date"
                    value={values.plan_start_date}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      setFieldValue('plan_start_date', newStartDate);

                      if (values.plan_end_date) {
                        const selectedStart = new Date(newStartDate);
                        const selectedEnd = new Date(values.plan_end_date);

                        // Add 1 day to start date
                        selectedStart.setDate(selectedStart.getDate() + 1);

                        if (selectedEnd < selectedStart) {
                          // If end date is before (start + 1 day), reset it
                          setFieldValue('plan_end_date', '');
                        }
                      }
                    }}
                    className={`shadow-none ${errors?.plan_start_date && touched.plan_start_date ? "error" : "correct"}`}
                  />

                  {touched.plan_start_date && errors?.plan_start_date && (
                    <ValidationFeedback title={errors?.plan_start_date} />
                  )}
                </Col>

                <Col md={6} className="mb-4">
                  <CommonFormLabel htmlFor="plan_end_date" title="Plan End Date" />
                  <Input
                    id="plan_end_date"
                    name="plan_end_date"
                    type="date"
                    value={values.plan_end_date}
                    onChange={handleChange}
                    min={
                      values.plan_start_date
                        ? new Date(new Date(values.plan_start_date).setDate(new Date(values.plan_start_date).getDate() + 1))
                            .toISOString()
                            .split('T')[0]
                        : new Date().toISOString().split('T')[0]
                    }
                  />
                  {touched.plan_end_date && errors?.plan_end_date && (
                    <ValidationFeedback title={errors?.plan_end_date} />
                  )}
                </Col>


                <Col md={6} className="">
                  <SimpleDropdown
                    label="Plan Status"
                    name="plan_status"
                    value={values.plan_status}
                    onChange={(selectedOption) => {
                      setFieldValue("plan_status", selectedOption.value);
                    }}
                    options={planStatusOptions}
                    placeholder="Select a plan status"
                    error={errors?.plan_status}
                    touched={touched.plan_status}
                    className={'mb-4'}
                  />
                </Col>
              </Row>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">Image</h4>
              <Row>
                <Col md={6} className="mb-4">
                  <div className="mb-4">
                    <CommonFormLabel htmlFor="logo" title="Upload Logo" />
                    <FileInput
                      name="logo"
                      onChange={(file) =>
                        handleImageChange(file, setFieldValue)
                      }
                      error={errors?.logo}
                      touched={touched.logo}
                      inputType="image"
                    />
                    {touched.logo && errors?.logo && (
                      <ValidationFeedback title={errors?.logo} />
                    )}
                    {logoPreview && (
                      <div className="mt-3">
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          style={{
                            width: "200px",
                            height: "auto",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Col>

                <Col md={6} className="mb-4">
                  <div className="mb-4">
                    <CommonFormLabel htmlFor="favicon_icon" title="Favicon" />
                    <FileInput
                      name="favicon_icon"
                      onChange={(file) =>
                        handleFavIconChange(file, setFieldValue)
                      }
                      error={errors?.favicon_icon}
                      touched={touched.favicon_icon}
                      inputType="image"
                    />
                    {touched.favicon_icon && errors?.favicon_icon && (
                      <ValidationFeedback title={errors?.favicon_icon} />
                    )}
                    {favIconPreview && (
                      <div className="mt-3">
                        <img
                          src={favIconPreview}
                          alt="Favicon Icon Preview"
                          style={{
                            width: "200px",
                            height: "auto",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>

            <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
              <CommonButton
                title={isSubmitting ? "Adding..." : "Add"}
                type="submit"
                disabled={isSubmitting}
                className="btn-primary primary-shadow"
              />
              <CommonButton
                title="Cancel"
                onClick={handleModalClose}
                className="btn-muted"
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
